// stages.ts — the 11-stage public roadmap for the relational trust
// infrastructure research program. Stages 0-10. Each entry is rendered by
// <StageCard /> on /research/roadmap.
//
// Status taxonomy is intentionally narrow. "drafted" means a complete
// manuscript exists; "scaffolded" means structure and equations are in place
// but the writing is partial; "in-development" means active work on a
// non-paper deliverable; "hypothesis" means the prediction has not been
// tested; "conceptual" means the argument is mapped but not written;
// "not-ready" means gated on prior modules. "submitted" / "under-review" /
// "accepted" advance Paper 1 once 2026 lands.

export type StageStatus =
  | "drafted"
  | "scaffolded"
  | "in-development"
  | "hypothesis"
  | "conceptual"
  | "not-ready"
  | "submitted"
  | "under-review";

export type Stage = {
  /** "S0" .. "S10" — used for keys and the slug fragment. */
  id: string;
  /** 0..10 — display order and the leading number. */
  n: number;
  /** Title rendered as the card heading. */
  title: string;
  /** One of the StageStatus values above. */
  status: StageStatus;
  /** 12-25 word claim — what the module proves or asks. */
  claim: string;
  /** 1-2 sentence framing — what unblocks it / what it unblocks. */
  why: string;
  /** 1-2 sentence definition of "done." */
  gate: string;
  /** Optional submission/launch window (kept calendar-light by design). */
  window?: string;
  /** Optional paper id to cross-link to /research/papers. */
  paperId?: string;
};

export const STAGES: Stage[] = [
  {
    id: "S0",
    n: 0,
    title: "Program Architecture",
    status: "in-development",
    claim:
      "The program presents itself before it asks to be evaluated, so scope, sequence, and falsification conditions are visible from the first click.",
    why: "Nothing else in the program can be read accurately until the structure surrounding it is legible. The architecture is the precondition for honest review.",
    gate: "A complete public surface — Program page, Roadmap, Papers, Methods, Risks, and Falsification — each navigable and aligned on the same module taxonomy.",
  },
  {
    id: "S1",
    n: 1,
    title: "Paper 1: Ostrom-Percolation",
    status: "drafted",
    claim:
      "Ostrom's eight design principles can be read as maintenance functions on a trust network whose connectivity follows a percolation threshold.",
    why: "This is the paper that anchors the program. Until it is submitted, every later module is speculative attachment to an unsubmitted core.",
    gate: "Manuscript v1.0, supplement, code and data DOI, and cover letter complete. Primary venue: a commons-governance journal. Fallback: a complex-systems journal.",
    window: "Submission window: 2026",
    paperId: "P1",
  },
  {
    id: "S2",
    n: 2,
    title: "Public Research Site",
    status: "in-development",
    claim:
      "A research program that intends to be testable should be readable in public before it asks the public to test it.",
    why: "The site exists to host the work, not to precede it. It launches the moment Paper 1 is submission-ready, and not before.",
    gate: "Site live with Program, Roadmap, Papers, Methods, Risks, and Falsification. Limitations and falsifiers surfaced, not buried.",
  },
  {
    id: "S3",
    n: 3,
    title: "Reconciliation Dynamics (Paper 2)",
    status: "scaffolded",
    claim:
      "Betrayal and repair can be modeled as coupled dynamics — a betrayal accumulator with decay, a recovery term, and identifiable bifurcation thresholds.",
    why: "Reconciliation is the narrowest extractable mechanism inside the larger trust equation. Publishing it alone tests whether the math survives outside its parent theory.",
    gate: "Mathematical paper with phase portraits, bifurcation analysis, and a minimal simulation, submitted to a complex-systems or applied-math venue.",
    paperId: "P2",
  },
  {
    id: "S4",
    n: 4,
    title: "Trust Force Equation (Paper 3)",
    status: "drafted",
    claim:
      "A relational trust score from operational variables — capacity, presence, reciprocity, betrayal, decay — comparable head-to-head against existing computational trust models.",
    why: "The equation cannot be the program's flagship until its variables are coded by people other than the author. Operational definitions are the precondition for the theory paper to count as theory.",
    gate: "Coding manual complete, two or more external coders trained, inter-rater reliability calculated and reported, score benchmarked against Marsh, Jøsang, and FIRE on shared cases.",
    paperId: "P3",
  },
  {
    id: "S5",
    n: 5,
    title: "Presence and Attestation",
    status: "in-development",
    claim:
      "Presence decomposes into proximity and attention, amplified by creation and witness — and this decomposition is what separates attestation from surveillance.",
    why: "Presence is the variable inside the trust equation that does the most work and is the least defined. It earns a short paper before it earns a chapter.",
    gate: "A short theory paper or essay defining the four-term decomposition, the surveillance-versus-attestation distinction, and three test cases drawn from existing institutions.",
  },
  {
    id: "S6",
    n: 6,
    title: "Temporal Edge Persistence (DP9 Empirical)",
    status: "hypothesis",
    claim:
      "Periodic renewal of relational ties predicts edge persistence above a measurable threshold, in at least one bounded empirical domain.",
    why: "DP9 is the program's first claim that can be falsified with field data. It cannot be promoted past hypothesis until one domain is studied end to end.",
    gate: "A single domain selected — acequia, open source, Wikipedia, small-scale fisheries, or mutual aid — with a pre-registered hypothesis, dataset, analysis plan, and result reported regardless of direction.",
    paperId: "P4",
  },
  {
    id: "S7",
    n: 7,
    title: "Governance Diagnostics",
    status: "conceptual",
    claim:
      "The framework can be reduced to a practitioner audit — a checklist that lets a working institution locate its own failure modes against the design principles.",
    why: "Diagnostics belong after the underlying theory has been peer-reviewed at least once. A premature audit tool would carry more authority than the science behind it.",
    gate: "A field-tested audit instrument with one institutional partner who has used it and reported back, in writing, on what it caught and what it missed.",
  },
  {
    id: "S8",
    n: 8,
    title: "AI Institutional Alignment",
    status: "conceptual",
    claim:
      "Alignment of AI systems is an institutional problem before it is a technical one, and the design-principle frame names what current alignment proposals are missing.",
    why: "This module is held back on purpose. Publishing it before Paper 1 would let the program be read as AI commentary rather than as institutional theory.",
    gate: "Position paper drafted only after Paper 1 is in review, and only after at least one of the empirical or operational modules has cleared its own gate.",
    paperId: "P5",
  },
  {
    id: "S9",
    n: 9,
    title: "Three-Force Scaling",
    status: "drafted",
    claim:
      "Trust, information, and physical constraint behave as three coupled forces, and institutional resilience is the rate at which a system cycles between them.",
    why: "Synthesis is allowed only after the components hold up alone. This module sits behind Stages 3, 4, and 6 in the queue by design.",
    gate: "A theory paper or long-form essay that integrates the three forces only with reference to modules that have already been independently published or submitted.",
  },
  {
    id: "S10",
    n: 10,
    title: "General Theory Synthesis",
    status: "not-ready",
    claim:
      "A monograph that integrates the modules into a single account of relational trust infrastructure, written only after each module has stood on its own.",
    why: "The synthesis is last, not first. Treating it as the destination is the discipline that keeps every preceding module honest.",
    gate: "Each prior module independently legible, independently testable, and independently published or accepted. Until then, the general theory remains a program-level frame, not a claim.",
  },
];
